// SHADOW halt reminder (ISS-031, 3 Nov 2026). While SHADOW is halted NOTHING shadow is sent - entries, hedges, exits.
// A popup explains it once per halt (keyed on when it started); after "I understand" a red strip stays above every page
// and the header warning count includes it, so it is always discoverable without interrupting again. Clearing stays a
// human action on Shadow Live - nothing here clears or bypasses the halt.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { visibleInterval } from "@/lib/poll";

const SEEN_KEY = "gz_shadow_halt_seen";
const POLL_MS = 30000;

export function useShadowHalt(enabled = true) {
  const [halt, setHalt] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const load = () => api.get("/paper-book/shadow-halt").then((r) => { if (live) setHalt(r.data); }).catch(() => {});
    load();
    const stop = visibleInterval(load, POLL_MS);
    return () => { live = false; stop(); };
  }, [enabled]);
  return halt?.halted ? halt : null;
}

const when = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString("en-IN", { hour12: false });
};

export default function ShadowHaltAlert({ halt }) {
  const key = halt ? String(halt.since || halt.reason || "halt") : null;
  const [seen, setSeen] = useState(() => { try { return localStorage.getItem(SEEN_KEY); } catch { return null; } });
  if (!halt) return null;
  const popup = seen !== key;
  const acknowledge = () => { try { localStorage.setItem(SEEN_KEY, key); } catch { /* optional */ } setSeen(key); };
  const account = halt.scope === "ALL_ACCOUNTS" ? "every SHADOW account" : `account ${halt.account?.label || halt.account?.id || "—"}`;
  return (
    <>
      <div className="bg-[#FF3B30] text-white text-xs px-4 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="shadow-halt-strip">
        <b>SHADOW HALTED</b>
        <span className="truncate max-w-[60ch]" title={halt.reason}>{halt.reason}</span>
        <span>· {account} · since {when(halt.since)}</span>
        <Link to="/shadow-live" className="underline font-semibold">Review on Shadow Live</Link>
        <button type="button" className="underline" onClick={() => setSeen(null)} data-testid="shadow-halt-details">Details</button>
      </div>
      {popup && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="SHADOW halted"
          data-testid="shadow-halt-popup">
          <div className="term-panel-2 rounded-md max-w-lg w-full p-5 space-y-3 border-2 border-[#FF3B30]">
            <h2 className="text-base font-bold text-[#FF3B30]">SHADOW trading is halted</h2>
            <div className="text-sm space-y-2">
              <p><span className="text-[var(--term-muted)]">Reason </span><b>{halt.reason}</b></p>
              <p><span className="text-[var(--term-muted)]">Affects </span>{account}<span className="text-[var(--term-muted)]"> · since </span>{when(halt.since)}</p>
              <p>{halt.action}</p>
              <p className="text-[var(--term-muted)] text-xs">Open SHADOW trades are not squared off and their prices keep updating, but no order of any kind is sent.
                The halt is never cleared automatically.</p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Link to="/shadow-live" onClick={acknowledge} className="px-3 py-1.5 rounded-sm bg-[#FF3B30] text-white text-xs font-bold"
                data-testid="shadow-halt-go">Go to Shadow Live</Link>
              <button type="button" onClick={acknowledge} className="px-3 py-1.5 rounded-sm border border-[var(--term-border)] text-xs"
                data-testid="shadow-halt-ack">I understand</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
